import { cache } from 'react';
import { notFound } from 'next/navigation';
import { api } from '@weareconceptstudio/core';

export const revalidate = 3600;

// ======================== Global ======================== //
export const getGlobal = cache(async ({ lang }) => {
	return await api.get({ url: 'global-data', lang });
});

// ======================== Pages ======================== //
export const getPage = cache(async ({ slug, lang, params = {} }) => {
	return await api.get({ url: `${slug}`, lang, params }).catch((err) => {
		notFound();
	});
});

// ======================== Meta ======================== //
export const getMeta = cache(async ({ slug, lang }) => {
	return await api.get({ url: `seo/${slug}`, lang });
});

// ======================== Faqs ======================== //
export const getFaqs = cache(async ({ lang }) => {
	const { data } = await api.get({ url: 'faqs', lang });

	return data;
});

// ======================== Privacy Policies ======================== //
export const getPrivacyPolicies = cache(async ({ lang }) => {
	const { data } = await api.get({ url: 'privacy-policies', lang });

	return data;
});

// ======================== Terms ======================== //
export const getTerms = cache(async ({ lang }) => {
	const { data } = await api.get({ url: 'terms', lang });

	return data;
});

// ======================== News ======================== //
export const getPosts = cache(async ({ currentPostId, lang, params = {} }) => {
	return await api.get({
		url: 'posts',
		lang,
		params: {
			...params,
			currentPostId,
		},
	});
});

// ======================== News Individual ======================== //
export const getNewsIndividual = async ({ slug, lang, params = {} }) => {
	try {
		const data = await api.get({ url: `posts/${slug}`, lang, params });

		if (!data) {
			notFound();
		}

		return data;
	} catch (err) {
		notFound();
	}
};

// ======================== Products ======================== //
export const getProducts = cache(async ({ currentProductId, lang, params = {} }) => {
	return await api.get({
		url: 'products',
		lang,
		params: {
			...params,
			product_Id: currentProductId,
		},
	});
});

// ======================== Products Individual ======================== //
export const getProductsIndividual = cache(async ({ slug, lang, params = {} }) => {
	try {
		const data = await api.get({ url: `products/${slug}`, lang, params });

		if (!data) {
			notFound();
		}

		return data;
	} catch (err) {
		notFound();
	}
});

// ======================== Recipes ======================== //
export const getRecipes = cache(async ({ lang, params = {} }) => {
	return await api.get({ url: 'recipes', lang, params });
});

// ======================== Recipes Tags ======================== //
export const getRecipesTags = cache(async ({ lang, params = {} }) => {
	return await api.get({ url: 'recipe-tags', lang, params });
});

// ======================== Recipes Individual ======================== //
export const getRecipesIndividual = cache(async ({ slug, lang, params = {} }) => {
	try {
		const data = await api.get({ url: `recipes/${slug}`, lang, params });

		if (!data) {
			notFound();
		}

		return data;
	} catch (err) {
		notFound();
	}
});

// ======================== Recipes Related  ======================== //
export const getRecipesRelated = cache(async ({ slug, lang }) => {
	try {
		const { data } = await api.get({ url: `recipes/${slug}/related-products`, lang });

		if (!data) {
			notFound();
		}

		return data;
	} catch (err) {
		notFound();
	}
});

// ======================== Latest Related Recipes ======================== //
export const getLatestRelatedRecipes = cache(async ({ lang }) => {
	try {
		const { data } = await api.get({ url: `recipes?latest`, lang });

		if (!data) {
			notFound();
		}

		return data;
	} catch (err) {
		notFound();
	}
});

// ======================== Recipes Related Product ======================== //
export const getRecipesRelatedProduct = cache(async ({ id, lang }) => {
	try {
		const { data } = await api.get({ url: `related-recipes?product_id=${id}`, lang });

		if (!data) {
			notFound();
		}

		return data;
	} catch (err) {
		notFound();
	}
});

let cacheData = new Map();

let funcs = {
	faqs: getFaqs,
	privacyPolicies: getPrivacyPolicies,
	terms: getTerms,
};

export function fetchData({ name, lang, params = {} }) {
	const dataKey = `${lang}-${name}`;

	if (!cacheData.has(dataKey)) {
		cacheData.set(dataKey, funcs[name]?.({ lang, ...params }));
	}

	return cacheData.get(dataKey);
}
